/**
 * The quartermaster's puzzles without React: what each round deals and the rules that judge it,
 * kept apart from the boards so the tests can deal thousands of rounds and check each one is fair.
 *
 *  - COUNT THE STOCK: which crates roll past, which mark to count, when each comes out.
 *  - BALANCE THE SCALES: a load and a handful of weights, always dealt from a known answer.
 *  - PACK THE CRATE: a full crate cut into blocks, so the blocks always fill it exactly.
 *  - FAIR SHARES: where each sliding bar is, and whether a tap cut it fair.
 */
import type { Level } from '../kit.js';
import { easier } from '../kit/levels.js';

const pick = <T>(xs: readonly T[], rand: () => number): T => xs[Math.floor(rand() * xs.length)]!;
const between = (lo: number, hi: number, rand: () => number) => lo + Math.floor(rand() * (hi - lo + 1));

function shuffle<T>(xs: T[], rand: () => number): T[] {
  for (let i = xs.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [xs[i], xs[j]] = [xs[j]!, xs[i]!];
  }
  return xs;
}

// -- COUNT THE STOCK ---------------------------------------------------------

export type Mark = 'star' | 'spark' | 'tri' | 'vee' | 'plus' | 'cross' | 'dot' | 'ring' | 'square';

/** The marks a round may ask for, each with the one that looks like it (round 3's trap). */
export const LOOKALIKE = { star: 'spark', tri: 'vee', plus: 'cross' } as const satisfies Record<string, Mark>;
export type TargetMark = keyof typeof LOOKALIKE;
export const TARGET_MARKS: readonly TargetMark[] = ['star', 'tri', 'plus'];
/** Marks nothing like any of the targets. */
export const OTHER_MARKS: readonly Mark[] = ['dot', 'ring', 'square'];

export interface Belt {
  crates: number;
  /** How many carry the mark, at least and at most. */
  marked: readonly [number, number];
  /** The belt's speed, in board widths a second. */
  speed: number;
  /** Seconds between two crates, on average. */
  every: number;
  /** 0: the rest are plain; 1: some carry other marks; 2: some carry the mark's look-alike. */
  decoys: 0 | 1 | 2;
  /** The answer buttons, lowest and highest. */
  answers: readonly [number, number];
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const BELTS: readonly Belt[] = easier<Belt>([
  { crates: 6, marked: [2, 4], speed: 0.28, every: 0.95, decoys: 0, answers: [1, 6] },
  { crates: 8, marked: [3, 5], speed: 0.34, every: 0.8, decoys: 1, answers: [2, 7] },
]);

/** A crate's width as a share of the board's, and the least free belt between two crates. */
export const CRATE_W = 0.21;
export const CRATE_GAP = 0.06;
/** The first crate comes out after this long, so the belt is seen to start first. */
export const FIRST_CRATE_S = 0.7;

export interface Stock {
  target: TargetMark;
  crates: readonly (Mark | null)[];
  /** When each crate comes out onto the belt, in seconds from the start of the run. */
  enter: readonly number[];
  /** The right answer: how many crates carry the target mark. */
  count: number;
}

export function dealStock(level: Level, rand: () => number): Stock {
  const belt = BELTS[level]!;
  const target = pick(TARGET_MARKS, rand);
  const count = between(belt.marked[0], belt.marked[1], rand);
  const rest = belt.crates - count;
  let decoys: Mark[] = [];
  if (belt.decoys === 1) {
    decoys = Array.from({ length: Math.min(rest, 2 + Math.floor(rand() * 2)) }, () => pick(OTHER_MARKS, rand));
  } else if (belt.decoys === 2) {
    const look = Math.min(rest, 2 + Math.floor(rand() * 2));
    decoys = [...Array.from({ length: look }, () => LOOKALIKE[target]), ...(rest > look ? [pick(OTHER_MARKS, rand)] : [])];
  }
  const crates: (Mark | null)[] = shuffle(
    [...Array.from({ length: count }, () => target), ...decoys, ...Array.from({ length: rest - decoys.length }, () => null)],
    rand,
  );
  const least = (CRATE_W + CRATE_GAP) / belt.speed;
  const enter: number[] = [];
  let t = FIRST_CRATE_S;
  for (let i = 0; i < crates.length; i++) {
    enter.push(t);
    t += Math.max(least, belt.every * (0.8 + 0.4 * rand()));
  }
  return { target, crates, enter, count };
}

/** The numbers on the answer buttons. */
export function answerRow(level: Level): number[] {
  const [lo, hi] = BELTS[level]!.answers;
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

// -- BALANCE THE SCALES ------------------------------------------------------

export type Side = 'L' | 'R';
export const other = (side: Side): Side => (side === 'L' ? 'R' : 'L');

export interface ScaleLevel {
  /** How many weights lie below the scales. */
  weights: number;
  /** How many of them the answer puts on the far pan, and how many beside the load. */
  far: readonly [number, number];
  near: number;
  /** The weights' numbers, lowest and highest. */
  lo: number;
  hi: number;
  /** The load, lightest and heaviest. */
  loadMin: number;
  loadMax: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const SCALE_LEVELS: readonly ScaleLevel[] = easier([
  { weights: 3, far: [2, 2], near: 0, lo: 1, hi: 9, loadMin: 3, loadMax: 17 },
  { weights: 4, far: [2, 3], near: 0, lo: 2, hi: 12, loadMin: 5, loadMax: 26 },
]);

export interface Scale {
  load: number;
  loadSide: Side;
  weights: readonly number[];
  /** One way to level it: where each weight goes (null stays below). */
  solution: readonly (Side | null)[];
}

/** Every total a subset of the weights can make (the empty one left out). */
export function subsetSums(weights: readonly number[]): Set<number> {
  let sums = new Set<number>([0]);
  for (const w of weights) sums = new Set([...sums, ...[...sums].map((s) => s + w)]);
  sums.delete(0);
  return sums;
}

/** Right pan minus left pan: above 0 the right side is down. */
export function balanceOf(scale: Pick<Scale, 'load' | 'loadSide' | 'weights'>, placed: readonly (Side | null)[]): number {
  let right = scale.loadSide === 'R' ? scale.load : 0;
  let left = scale.loadSide === 'L' ? scale.load : 0;
  scale.weights.forEach((w, i) => {
    if (placed[i] === 'R') right += w;
    if (placed[i] === 'L') left += w;
  });
  return right - left;
}

/** Whether some weights on the far pan alone can level it (round 3 must not allow that). */
export const oneSided = (load: number, weights: readonly number[]) => subsetSums(weights).has(load);

export function dealScale(level: Level, rand: () => number): Scale {
  const lv = SCALE_LEVELS[level]!;
  for (let attempt = 0; attempt < 500; attempt++) {
    const pool = shuffle(Array.from({ length: lv.hi - lv.lo + 1 }, (_, i) => lv.lo + i), rand);
    const weights = pool.slice(0, lv.weights);
    const far = between(lv.far[0], lv.far[1], rand);
    const order = shuffle(weights.map((_, i) => i), rand);
    const farIdx = order.slice(0, far);
    const nearIdx = order.slice(far, far + lv.near);
    const load = farIdx.reduce((s, i) => s + weights[i]!, 0) - nearIdx.reduce((s, i) => s + weights[i]!, 0);
    if (load < lv.loadMin || load > lv.loadMax) continue;
    // Never one weight that is the load, and in round 3 no way to do it on one pan.
    if (weights.includes(load)) continue;
    if (lv.near > 0 && oneSided(load, weights)) continue;
    const loadSide: Side = rand() < 0.5 ? 'L' : 'R';
    const solution = weights.map((_, i) => (farIdx.includes(i) ? other(loadSide) : nearIdx.includes(i) ? loadSide : null));
    return { load, loadSide, weights, solution };
  }
  // Unreachable in practice; a fixed fair deal just in case.
  return level === 2
    ? { load: 17, loadSide: 'L', weights: [9, 3, 8, 11], solution: ['R', null, 'R', null] }
    : { load: 8, loadSide: 'L', weights: [3, 5, 4], solution: ['R', 'R', null] };
}

// -- PACK THE CRATE ----------------------------------------------------------

/** A cell of a block or the crate: column, then row (rows run down). */
export type Cell = readonly [number, number];

export interface CrateLevel {
  cols: number;
  rows: number;
  /** The blocks' sizes, in cells; they add up to the crate. */
  sizes: readonly number[];
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const CRATE_LEVELS: readonly CrateLevel[] = easier([
  { cols: 3, rows: 3, sizes: [3, 3, 3] },
  { cols: 4, rows: 3, sizes: [4, 3, 3, 2] },
]);

/** A block's width and height in cells. */
export function extent(cells: readonly Cell[]): [number, number] {
  return [Math.max(...cells.map((c) => c[0])) + 1, Math.max(...cells.map((c) => c[1])) + 1];
}

/** Shifted to start at 0,0 and sorted, so two drawings of the same block compare equal. */
export function normalise(cells: readonly Cell[]): Cell[] {
  const c0 = Math.min(...cells.map((c) => c[0]));
  const r0 = Math.min(...cells.map((c) => c[1]));
  return cells.map((c): Cell => [c[0] - c0, c[1] - r0]).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
}

/** A quarter turn clockwise. */
export function turn(cells: readonly Cell[]): Cell[] {
  const [, h] = extent(cells);
  return normalise(cells.map((c): Cell => [h - 1 - c[1], c[0]]));
}

export function turned(cells: readonly Cell[], turns: number): Cell[] {
  let out = normalise(cells);
  for (let i = 0; i < ((turns % 4) + 4) % 4; i++) out = turn(out);
  return out;
}

export const key = (col: number, row: number) => `${col},${row}`;

/** Whether a block laid with its top left at `at` stays inside the crate and on empty cells. */
export function fits(cells: readonly Cell[], at: Cell, filled: ReadonlySet<string>, cols: number, rows: number): boolean {
  return cells.every(([c, r]) => {
    const col = at[0] + c;
    const row = at[1] + r;
    return col >= 0 && row >= 0 && col < cols && row < rows && !filled.has(key(col, row));
  });
}

export interface PackedPiece {
  /** The block as it lies in the answer. */
  cells: readonly Cell[];
  /** Where the answer lays it: its top left cell. */
  at: Cell;
  /** Quarter turns it is dealt with in the tray. */
  turns: number;
}

/** The largest a block may be across, in cells: a straight four is too long for the tray. */
export const MAX_EXTENT = 3;

/** Cuts a full crate into blocks of the level's sizes, so they always fill it exactly. */
export function cutCrate(level: Level, rand: () => number): PackedPiece[] {
  const lv = CRATE_LEVELS[level]!;
  for (let attempt = 0; attempt < 400; attempt++) {
    const pieces = carve(lv, shuffle([...lv.sizes], rand), rand);
    if (!pieces) continue;
    return shuffle(
      pieces.map((raw) => {
        const cells = normalise(raw);
        const at: Cell = [Math.min(...raw.map((c) => c[0])), Math.min(...raw.map((c) => c[1]))];
        return { cells, at, turns: Math.floor(rand() * 4) };
      }),
      rand,
    );
  }
  // Unreachable in practice: a fixed fair fill.
  return FALLBACK_FILLS[level].map((raw) => ({
    cells: normalise(raw),
    at: [Math.min(...raw.map((c) => c[0])), Math.min(...raw.map((c) => c[1]))] as Cell,
    turns: 1,
  }));
}

/** Fixed fills, one a round, each the shape of that round's crate. */
const FALLBACK_FILLS: Record<Level, readonly (readonly Cell[])[]> = {
  0: [
    [[0, 0], [1, 0], [2, 0]],
    [[0, 1], [0, 2], [1, 2]],
    [[1, 1], [2, 1], [2, 2]],
  ],
  1: [
    [[0, 0], [1, 0], [2, 0]],
    [[0, 1], [0, 2], [1, 2]],
    [[1, 1], [2, 1], [2, 2]],
  ],
  2: [
    [[0, 0], [1, 0], [0, 1], [1, 1]],
    [[2, 0], [3, 0], [2, 1]],
    [[3, 1], [3, 2]],
    [[0, 2], [1, 2], [2, 2]],
  ],
};

function carve(lv: CrateLevel, sizes: readonly number[], rand: () => number): Cell[][] | null {
  const taken = new Set<string>();
  const out: Cell[][] = [];
  for (const size of sizes) {
    // Grow each block from the first empty cell in reading order, so no gap is left behind it.
    let seed: Cell | null = null;
    for (let r = 0; r < lv.rows && !seed; r++) for (let c = 0; c < lv.cols && !seed; c++) if (!taken.has(key(c, r))) seed = [c, r];
    if (!seed) return null;
    const piece: Cell[] = [seed];
    taken.add(key(...seed));
    while (piece.length < size) {
      const frontier = new Map<string, Cell>();
      for (const [c, r] of piece) {
        for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const n: Cell = [c + dc, r + dr];
          if (n[0] < 0 || n[1] < 0 || n[0] >= lv.cols || n[1] >= lv.rows || taken.has(key(...n))) continue;
          frontier.set(key(...n), n);
        }
      }
      if (frontier.size === 0) return null;
      const next = pick([...frontier.values()], rand);
      piece.push(next);
      taken.add(key(...next));
    }
    const [w, h] = extent(normalise(piece));
    if (Math.max(w, h) > MAX_EXTENT) return null;
    out.push(piece);
  }
  return taken.size === lv.cols * lv.rows ? out : null;
}

// -- FAIR SHARES -------------------------------------------------------------

export interface ShareLevel {
  bars: number;
  shares: 2 | 3;
  /** How long each bar takes to slide there and back, in ms. */
  periods: readonly number[];
  /** A bar's length, as a share of its track. */
  barWidth: number;
  /** Half the light band round each fair cut, as a share of the bar. */
  band: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const SHARE_LEVELS: readonly ShareLevel[] = easier<ShareLevel>(
  [
    { bars: 2, shares: 2, periods: [5_000, 5_800], barWidth: 0.6, band: 0.12 },
    { bars: 3, shares: 2, periods: [3_800, 4_400, 3_400], barWidth: 0.58, band: 0.1 },
  ],
  // Two bars, each sliding at a pace halfway between the old rounds.
  { periods: [4_400, 5_100] },
);

/** Past the band's edge a tap still counts for this much more: a thumb is not a pin. */
export const SHARE_GRACE = 0.035;

/** Where the fair cuts fall: ½ for halves; ⅓ and ⅔ for thirds. */
export const shareTargets = (shares: number) => Array.from({ length: shares - 1 }, (_, i) => (i + 1) / shares);

/** A bar's left end on its track (0 to 1 − barWidth), easing in and out at each end. */
export function barLeft(barWidth: number, period: number, phase: number, t: number): number {
  return (1 - barWidth) * (0.5 + 0.5 * Math.sin((2 * Math.PI * t) / period + phase));
}

export type CutVerdict = { kind: 'off' } | { kind: 'cut'; target: number; at: number } | { kind: 'miss'; at: number };

/**
 * A tap at `onTrack` (0–1 along the track) on a bar whose left end is at `left`: beside the bar
 * or on a cut already made is nothing; in (or just by) a band still to cut is that cut; anywhere
 * else on the bar is a wrong cut.
 */
export function judgeCut(onTrack: number, left: number, lv: Pick<ShareLevel, 'barWidth' | 'band' | 'shares'>, made: readonly number[]): CutVerdict {
  const at = (onTrack - left) / lv.barWidth;
  if (at < 0 || at > 1) return { kind: 'off' };
  const reach = lv.band + SHARE_GRACE;
  const targets = shareTargets(lv.shares);
  let best = -1;
  targets.forEach((t, i) => {
    if (Math.abs(at - t) > reach) return;
    if (best < 0 || Math.abs(at - t) < Math.abs(at - targets[best]!)) best = i;
  });
  if (best >= 0) return made.includes(best) ? { kind: 'off' } : { kind: 'cut', target: best, at };
  return { kind: 'miss', at };
}
